import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { api } from '../../../lib/api';

type TimetableInfo = { id: number; name: string; classroom_id: number; gradebook_config_id: number; period_index: number };
type TimetableEntry = {
  id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  subject: string;
  room?: string | null;
  teacher_name?: string | null;
};

function weekdayIndexMonday0(date: Date): number {
  // JS: Sunday=0..Saturday=6 -> Monday=0..Sunday=6
  return (date.getDay() + 6) % 7;
}

function timeToMinutes(time: string): number {
  const [h, m] = (time || '0:0').split(':').map((x) => Number(x) || 0);
  return h * 60 + m;
}

function formatTime(time: string): string {
  try {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return time;
  }
}

interface Props {
  classId: number | null;
  gradebookConfigId: number | null;
  periodIndex: number;
  onOpenStudentSchedule?: () => void;
}

const StudentTodayOrarWidget: React.FC<Props> = ({ classId, gradebookConfigId, periodIndex, onOpenStudentSchedule }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timetables, setTimetables] = useState<TimetableInfo[]>([]);
  const [entries, setEntries] = useState<TimetableEntry[]>([]);

  const today = useMemo(() => new Date(), []);
  const todayIdx = useMemo(() => weekdayIndexMonday0(today), [today]);

  const todaysEntries = useMemo(() => {
    const list = entries.filter((e) => Number(e.day_of_week) === Number(todayIdx));
    list.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    return list;
  }, [entries, todayIdx]);

  const isCurrent = (e: TimetableEntry) => {
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    const start = timeToMinutes(e.start_time);
    const end = timeToMinutes(e.end_time);
    return cur >= start && cur <= end;
  };

  const loadToday = async () => {
    if (!classId || !gradebookConfigId) {
      setTimetables([]);
      setEntries([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/orar/student/classrooms/${classId}/timetables`, {
        params: { gradebook_config_id: gradebookConfigId },
      });
      const list = Array.isArray(resp.data) ? (resp.data as TimetableInfo[]) : [];
      const ordered = list.slice().sort((a, b) => Number(a.period_index ?? 0) - Number(b.period_index ?? 0));
      setTimetables(ordered);

      const tt = ordered.find((t) => Number(t.period_index) === Number(periodIndex)) || ordered[0] || null;
      if (!tt?.id) {
        setEntries([]);
        return;
      }

      const entriesResp = await api.get(`/api/orar/student/timetables/${tt.id}/entries`);
      setEntries(Array.isArray(entriesResp.data) ? (entriesResp.data as TimetableEntry[]) : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca orarul de azi.');
      setTimetables([]);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, gradebookConfigId, periodIndex]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Orarul de azi</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span>
              {today.toLocaleDateString('ro-RO', { weekday: 'long', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="RefreshCcw" iconPosition="left" onClick={loadToday} disabled={loading}>
            Reîncarcă
          </Button>
        </div>
      </div>

      {error && <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}

      {loading ? (
        <div className="text-sm text-muted-foreground">Se încarcă...</div>
      ) : !classId ? (
        <div className="text-sm text-muted-foreground">Selectează o clasă pentru a vedea orarul.</div>
      ) : !gradebookConfigId ? (
        <div className="text-sm text-muted-foreground">Selectează un catalog pentru a vedea orarul.</div>
      ) : todayIdx > 4 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Icon name="CalendarOff" size={40} className="text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground text-center">Nu ai ore programate azi</p>
        </div>
      ) : todaysEntries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Icon name="CalendarOff" size={40} className="text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground text-center">Nu ai ore programate azi</p>
        </div>
      ) : (
        <div className="space-y-3">
          {todaysEntries.map((e) => {
            const current = isCurrent(e);
            return (
              <div
                key={e.id}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  current ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{e.subject || '—'}</div>
                    <div className="text-xs text-muted-foreground truncate">{e.teacher_name || '—'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {current && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-md">
                        <Icon name="Circle" size={8} className="animate-pulse" />
                        Acum
                      </span>
                    )}
                    {onOpenStudentSchedule && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Calendar"
                        iconPosition="left"
                        onClick={onOpenStudentSchedule}
                      >
                        Orar
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    <span>
                      {formatTime(e.start_time)} - {formatTime(e.end_time)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 min-w-0">
                    <Icon name="MapPin" size={12} />
                    <span className="truncate">{(e.room || '').toString() || '—'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentTodayOrarWidget;
